import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AlertCircle, Check, ChevronDown, Info, Mail } from "react-feather";
import { useDispatch, useSelector } from "react-redux";
import { gatewayInstance } from "../axiosInstance";
import { formatFromJsonDate } from "../dateConfig";
import { notificationActions } from "../store/notification/notification-slice";
import Modal from "./Modal";
import ReadMoreText from "./ReadMoreText";
import ViewAllNotifications from "./ViewAllNotifications";
import WebSocketConnection from "../service/WebSocketConnection";
import { toast } from "react-toastify";
import { notificationSocketUrl, SERVICE_ENDPOINT, MODULES } from "../const";
import { hasDuplicateSubscriptions } from "../helper";
import useUserCompany from "../hooks/useUserCompany";
import { useNotifications } from "./notification/NotificationProvider";
import { acknowledgeNotification } from "../api/notificationApi";
import { useNavigate } from "react-router-dom";

const ACTION_CENTER_ROUTES = {
  6: "/action-center/unauthorized-stops",
};

function getNotificationAction(notification) {
  const { notEventType, notUrl } = notification;

  if (notEventType === 31 || notEventType === 33) {
    return notUrl ? { label: "Download", url: notUrl } : null;
  }

  if (notEventType === 6) {
    return {
      label: "Action Center",
      url: notUrl || ACTION_CENTER_ROUTES[6],
    };
  }

  return null;
}

function containsHtmlContent(text) {
  if (typeof text !== "string") return false;
  return /<\/?[a-z][\s\S]*>/i.test(text);
}

function getSeverity(notification) {
  const t = (notification.notType || "").toLowerCase();
  if (t === "error") return "error";
  if (t === "warn" || t === "warning") return "warning";
  return "info";
}

const SEVERITY_STYLES = {
  error: {
    accent: "#DC2626",
    badgeBg: "#FEE2E2",
    badgeColor: "#B91C1C",
    iconBg: "#FEE2E2",
    iconColor: "#DC2626",
  },
  warning: {
    accent: "#F59E0B",
    badgeBg: "#FEF3C7",
    badgeColor: "#B45309",
    iconBg: "#FEF3C7",
    iconColor: "#D97706",
  },
  info: {
    accent: "#10B981",
    badgeBg: "#D1FAE5",
    badgeColor: "#047857",
    iconBg: "#D1FAE5",
    iconColor: "#059669",
  },
};

function SeverityIcon({ severity, style }) {
  if (severity === "info") return <Check size={16} color={style.iconColor} />;
  return <AlertCircle size={16} color={style.iconColor} />;
}

function formatTimeAgo(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "";

  const diffSec = Math.floor((Date.now() - date.getTime()) / 1000);
  if (diffSec < 60) return "just now";

  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;

  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;

  const diffDay = Math.floor(diffHr / 24);
  return `${diffDay}d ago`;
}

function NotificationDropDown({
  showNotifications,
  setShowNotifications,
  openActionCenter,
}) {
  const notifications = useSelector((state) => state.notification.list);
  const dispatch = useDispatch();
  const [notifyId, setNotifyId] = useState("");

  const [expandedKey, setExpandedKey] = useState(null);
  const user = useSelector((state) => state.user);
  const auth = useSelector((state) => state.user.permissions[MODULES?.Master]);
  const viewNotificationPermission = auth?.NOTIFICATION_TOGGLE?.FORM?.VIEW;
  const event6ListRef = useRef([]);
  const userCompany = useUserCompany();
  const { addNotification } = useNotifications();
  const event6List = useSelector((state) => state.notification.event6List);
  const navigate = useNavigate();
  const [htmlModalNotification, setHtmlModalNotification] = useState(null);
  const htmlModalRef = useRef();

  //console.log("NOTIFICATION LIST --> ", notifications);

  useEffect(() => {
    event6ListRef.current = event6List;
  }, [event6List]);

  const wsConnection = useMemo(
    () => new WebSocketConnection(notificationSocketUrl, {}),
    [],
  );

  const notificationQueue = `/user/queue/liveNotifications/${user.username}`;
  const notificationMessageMapping = `${SERVICE_ENDPOINT.TRANSPORT}/web-notifications/fetch/${user.username}`;

  const liveNotification = useCallback((data) => {
    for (let notification of data) {
      if (notification.isNew) {
        if (notification.notType === "Error") {
          toast.error(notification.notTitle, {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
          });
        } else if (
          notification.notType === "Warn" ||
          notification.notType === "Warning"
        ) {
          toast.warn(notification.notTitle, {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
          });
        } else {
          toast.info(notification.notTitle, {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
          });
        }
      }
    }
  }, []);

  useEffect(() => {
    if (!viewNotificationPermission) return;
    (async () => {
      let list = [];
      try {
        let batchNo = -1;
        await wsConnection.connect();
        if (!hasDuplicateSubscriptions(wsConnection, notificationQueue)) {
          wsConnection.subscribe(notificationQueue, (data) => {
            // Check if data is broken down into batches.
            if (data.add) {
              // For preventing same batch being added multiple times.
              if (batchNo !== data.batchNo) {
                if (data.start) {
                  // If First batch initialize list and add batch data.
                  list = [];
                  list.push(...data.notificationReturnResponses);
                } else if (data.end) {
                  const event6 = list.filter((n) => n.notEventType === 6);
                  const others = list.filter((n) => n.notEventType !== 6);

                  // If Last batch add batch data to list.
                  list.push(...data.notificationReturnResponses);

                  // show new notification toasts.
                  if (others.length > 0) {
                    liveNotification(others);
                  }
                  // console.log("LIST --> ", list);
                  // console.log(
                  //   "LIST SORTED --> ",
                  //   list.sort((a, b) => b.notDate - a.notDate),
                  // );
                  // set the list to redux state .
                  dispatch(
                    notificationActions.setNotifications(
                      list
                        .map((item) => [Date.parse(item.notDate), item])
                        .sort((a, b) => b[0] - a[0])
                        .map((pair) => pair[1]),
                    ),
                  );

                  console.log("Other notifications inside other", others);
                  if (event6.length > 0) {
                    const existingIds = new Set(
                      event6ListRef.current.map((n) => n.id),
                    );
                    for (let notification of event6) {
                      addNotification(
                        notification.notId,
                        notification.notTitle,
                        notification.notType,
                        notification.notDate,
                      );

                      if (!existingIds.has(notification.notId)) {
                        dispatch(
                          notificationActions.addEvent6Notification({
                            id: notification.notId,
                            type: notification.notTitle,
                            notType: notification.notType,
                            vehicle: notification.vehicle || "Unknown",
                            driver: notification.driver || "N/A",
                            time: formatFromJsonDate(notification.notDate),
                            status: "Pending",
                          }),
                        );
                      }
                    }
                  }
                } else {
                  // If intermediate batch add batch data to list.
                  list.push(...data.notificationReturnResponses);
                }
                batchNo = data.batchNo;
              }
            } else {
              const event6 = data.notificationReturnResponses.filter(
                (n) => n.notEventType === 6,
              );
              const others = data.notificationReturnResponses.filter(
                (n) => n.notEventType !== 6,
              );
              const existingIds = new Set(
                event6ListRef.current.map((n) => n.id),
              );
              console.log("existing notifications inside event6", existingIds);
              if (event6.length > 0) {
                console.log("Event 6 notifications inside event6", event6);
                for (let notification of event6) {
                  addNotification(
                    notification.notId,
                    notification.notTitle,
                    notification.notType,
                    notification.notDate,
                  );

                  if (!existingIds.has(notification.notId)) {
                    dispatch(
                      notificationActions.addEvent6Notification({
                        id: notification.notId,
                        type: notification.notTitle,
                        notType: notification.notType,
                        vehicle: notification.vehicle || "Unknown",
                        driver: notification.driver || "N/A",
                        time: formatFromJsonDate(notification.notDate),
                        status: "Pending",
                      }),
                    );
                    console.log(
                      "Event 6 notification added to Redux store:",
                      notification,
                    );
                  }
                }
              }

              if (others.length > 0) {
                liveNotification(others);
              }
              dispatch(
                notificationActions.setNotifications(
                  data.notificationReturnResponses
                    .map((item) => [Date.parse(item.notDate), item])
                    .sort((a, b) => b[0] - a[0])
                    .map((pair) => pair[1]),
                ),
              );
            }
          });
        }
        const payLoad = {
          company: userCompany ? userCompany.comId : null,
        };
        wsConnection.send(
          notificationMessageMapping,
          {},
          JSON.stringify(payLoad),
        );
      } catch (e) {
        console.error(e);
      }
    })();
  }, [
    dispatch,
    user.username,
    wsConnection,
    liveNotification,
    notificationQueue,
    notificationMessageMapping,
    viewNotificationPermission,
  ]);

  useEffect(() => {
    (async () => {
      try {
        try {
          const data1 = {
            username: user.username,
            notId: notifyId,
          };
          if (notifyId !== null && notifyId !== "") {
            await acknowledgeNotification(data1);
          }
        } catch (error) {
          console.error("Error fetching notification data:", error);
        }
      } catch (error) {
        console.error("Error removing notification data:", error);
      }
    })();
  }, [notifyId]);

  const removeNotification = (e, index, id) => {
    // Prevent link from navigating to href
    e.preventDefault();
    setNotifyId(id);
    dispatch(notificationActions.removeNotification(index, id));

    // Update local storage
    dispatch(notificationActions.addToLocalStorage());

    setExpandedKey((current) => (current === (id ?? index) ? null : current));
  };

  const modalRef = useRef();
  const childRef = useRef();

  const callChildFunction = () => {
    if (childRef.current) {
      childRef.current.resetFilters();
    }
  };

  const openModal = () => {
    modalRef.current?.showModal();
  };

  const toggleExpanded = (key) => {
    setExpandedKey((current) => (current === key ? null : key));
  };

  return (
    <>
      <style>{`
        .notif-list {
          display: flex;
          flex-direction: column;
        }
        .notif-card {
          display: flex;
          border-left: 3px solid transparent;
          border-bottom: 1px solid #EEEEF1;
          background: #fff;
          transition: background 0.15s ease;
        }
        .notif-card:hover {
          background: #FAFAFB;
        }
        .notif-card-icon {
          flex-shrink: 0;
          width: 30px;
          height: 30px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 12px 10px 0 12px;
        }
        .notif-card-body {
          flex: 1;
          min-width: 0;
          padding: 12px 10px 12px 0;
          cursor: pointer;
        }
        .notif-card-top {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 8px;
        }
        .notif-card-title {
          font-weight: 600;
          font-size: 15px;
          color: #1F1F26;
        }
        .notif-card-time {
          font-size: 11px;
          color: #9A98A3;
          white-space: nowrap;
          flex-shrink: 0;
          margin-top: 1px;
        }
        .notif-card-desc {
          font-size: 13px;
          color: #55535E;
          margin-top: 2px;
        }
        .notif-card-meta {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-top: 8px;
          flex-wrap: wrap;
        }
        .notif-badge {
          font-size: 10.5px;
          font-weight: 700;
          letter-spacing: 0.02em;
          padding: 2px 7px;
          border-radius: 4px;
          text-transform: uppercase;
        }
        .notif-req {
          font-size: 12px;
          color: #595763;
        }
        .notif-chevron {
          flex-shrink: 0;
          margin-top: 2px;
          color: #B5B3BD;
          transition: transform 0.15s ease;
        }
        .notif-chevron.open {
          transform: rotate(180deg);
        }
        .notif-expanded {
          margin-top: 10px;
          padding-top: 10px;
          border-top: 1px solid #EEEEF1;
        }
        .notif-expanded-desc {
          font-size: 12.5px;
          color: #3F3D47;
          line-height: 1.5;
        }
        .notif-expanded-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          margin-top: 10px;
          flex-wrap: wrap;
        }
        .notif-expanded-date {
          font-size: 11px;
          color: #9A98A3;
        }
        .notif-actions-row {
          display: flex;
          gap: 8px;
        }
        .notif-btn-primary {
          border: none;
          border-radius: 6px;
          padding: 6px 13px;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          background: #4F46E5;
          color: #fff;
          transition: background 0.15s ease;
        }
        .notif-btn-primary:hover {
          background: #4338CA;
        }
        .notif-btn-outline {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          border: 1px solid #D7D7DE;
          background: #fff;
          border-radius: 6px;
          padding: 6px 13px;
          font-size: 12px;
          font-weight: 600;
          color: #3B3B45;
          cursor: pointer;
          transition: all 0.15s ease;
        }
        .notif-btn-outline:hover {
          background: #ECFDF5;
          border-color: #34D399;
          color: #059669;
        }
        .notif-header-count {
          background: #DC2626;
          color: #fff !important;
          font-size: 12px;
          font-weight: 400;
         border-radius: 999px;
          min-width: 16px;
          height: 18px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 7px 7px;
          margin-left: 6px;
        }
            .notif-header-title {
          font-size: 18px;
          font-weight: 600;
          color: #212121;
          display: flex;
          align-items: center;
        }
      `}</style>

      <div style={{ zIndex: 10001, position: "absolute" }}>
        {showNotifications && (
          <div className="notifications-dropdown">
            <div
              className="notification-dropdown-top"
              style={{ paddingTop: "16px", paddingBottom: "16px" }}
            >
              <div className="notif-header-title">
                Notifications
                {notifications.length > 0 && (
                  <div className="notif-header-count">
                    {notifications.length}
                  </div>
                )}
              </div>
              <Mail size={20} color="#212121" />
            </div>

            <div
              className="notif-list"
              style={{
                height: "350px",
                overflowY: "scroll",
              }}
            >
              {notifications.length === 0 ? (
                <center className="no-notification">
                  No notifications available
                </center>
              ) : (
                notifications.map((notification, index) => {
                  const action = getNotificationAction(notification);
                  const severity = getSeverity(notification);
                  const style = SEVERITY_STYLES[severity];
                  const key = notification.notId ?? index;
                  const isOpen = expandedKey === key;
                  const isHtmlDescription = containsHtmlContent(
                    notification.notDescription,
                  );

                  return (
                    <div
                      className="notif-card"
                      key={index}
                      style={{ borderLeftColor: style.accent }}
                    >
                      <div
                        className="notif-card-icon"
                        style={{ background: style.iconBg }}
                      >
                        <SeverityIcon severity={severity} style={style} />
                      </div>

                      <div
                        className="notif-card-body"
                        onClick={() => {
                          if (isHtmlDescription) {
                            setHtmlModalNotification(notification);
                            htmlModalRef.current?.showModal();
                          } else {
                            toggleExpanded(key);
                          }
                        }}
                      >
                        <div className="notif-card-top">
                          <div className="notif-card-title">
                            {notification.notEveTypeName ||
                              notification.notType}
                          </div>
                          <div className="notif-card-time">
                            {formatTimeAgo(notification.notDate)}
                          </div>
                        </div>

                        {!isOpen && (
                          <div className="notif-card-desc">
                            <ReadMoreText
                              text={
                                isHtmlDescription
                                  ? notification.notTitle
                                  : notification.notDescription
                              }
                              maxLength={60}
                              shouldExpand={false}
                            />
                          </div>
                        )}

                        <div className="notif-card-meta">
                          <span
                            className="notif-badge"
                            style={{
                              background: style.badgeBg,
                              color: style.badgeColor,
                            }}
                          >
                            {notification.notType}
                          </span>
                          {notification.notRequestCode && (
                            <span className="notif-req">
                              {notification.notRequestCode}
                            </span>
                          )}
                        </div>

                        {isOpen && (
                          <div className="notif-expanded">
                            <div className="notif-expanded-desc">
                              {notification.notDescription ||
                                notification.notTitle}
                            </div>
                            <div className="notif-expanded-footer">
                              <span className="notif-expanded-date">
                                {formatFromJsonDate(notification.notDate)}
                              </span>
                              <div className="notif-actions-row">
                                {action && (
                                  <button
                                    className="notif-btn-primary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (notification.notEventType === 6) {
                                        openActionCenter?.();
                                      } else {
                                        navigate(action.url);
                                      }
                                      setShowNotifications(false);
                                    }}
                                  >
                                    {action.label}
                                  </button>
                                )}
                                <button
                                  className="notif-btn-outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeNotification(
                                      e,
                                      index,
                                      notification.notId,
                                    );
                                  }}
                                >
                                  <Check size={13} />
                                  Acknowledge
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      <ChevronDown
                        size={16}
                        className={`notif-chevron ${isOpen ? "open" : ""}`}
                        style={{ marginRight: 12, marginTop: 14 }}
                      />
                    </div>
                  );
                })
              )}
            </div>

            <div className="notification-dropdown-bottom" onClick={openModal}>
              View all notifications
            </div>
          </div>
        )}
      </div>
      <Modal
        ref={modalRef}
        minWidth={"100vw"}
        minHeight={"100vh"}
        closeButton={true}
        onCloseCleanUp={callChildFunction}
      >
        <ViewAllNotifications
          notifications={notifications}
          ref={childRef}
          navigate={navigate}
          closeModal={() => modalRef.current.close()}
        />
      </Modal>
      <Modal
        ref={htmlModalRef}
        minWidth={"700px"}
        minHeight={"80vh"}
        closeButton={true}
        onCloseCleanUp={() => setHtmlModalNotification(null)}
      >
        {htmlModalNotification && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              minHeight: "76vh",
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div
              style={{
                padding: "4px 30px 10px 4px",
                borderBottom: "1px solid #EEEEF1",
              }}
            >
              <div style={{ fontWeight: 600, fontSize: 15 }}>
                {htmlModalNotification.notEveTypeName ||
                  htmlModalNotification.notType}
              </div>
              <div style={{ fontSize: 12, color: "#9A98A3" }}>
                {formatFromJsonDate(htmlModalNotification.notDate)}
              </div>
            </div>

            <iframe
              title="notification-html-preview"
              srcDoc={htmlModalNotification.notDescription}
              sandbox=""
              style={{ flex: 1, width: "100%", border: "none", marginTop: 10 }}
            />

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
                padding: "10px 4px 0 4px",
                borderTop: "1px solid #EEEEF1",
              }}
            >
              {getNotificationAction(htmlModalNotification) && (
                <button
                  className="notif-btn-primary"
                  onClick={() => {
                    if (htmlModalNotification.notEventType === 6) {
                      openActionCenter?.();
                    } else {
                      navigate(
                        getNotificationAction(htmlModalNotification).url,
                      );
                    }
                    htmlModalRef.current?.close();
                  }}
                >
                  {getNotificationAction(htmlModalNotification).label}
                </button>
              )}
              <button
                className="notif-btn-outline"
                onClick={(e) => {
                  const idx = notifications.findIndex(
                    (n) => n.notId === htmlModalNotification.notId,
                  );
                  removeNotification(e, idx, htmlModalNotification.notId);
                  htmlModalRef.current?.close();
                }}
              >
                <Check size={13} />
                Acknowledge
              </button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}

export default NotificationDropDown;
